import { Calculator, ShowErrorWay } from "../common/calculator";
import { DateField, DateIntervalField } from "../common/datetime_fields.js";
import { DatePoint, distinctDates } from '../js/datelib.js';

class LNPCalculator extends Calculator {
    code() { return "lnp"; }
    type() { return "CalcLoanPercent"; }

    initialize() {
        let self = this;
        this.patchFieldTypes([DateIntervalField, DateField]);
        this.setupStatistic({
            calcName: 'lnp'
        });

        // add input fields
        this.loanSumField = this.addInputField('#loanSum input', 'loanSum', {
            type: 'money', options: { maxVal: 999999999999.99 },
            errors: [{ check: function (v) { return !!v; }, selector: '#loanSum .error-text' }]
        });
        this.mainPeriodField = this.addInputField('.calc-div-input-main > .period-div', 'mainPeriod', {
            showErrorWay: ShowErrorWay.SHOW_FIRST,
        });
        this.addInputField('#percentRate .base-input', 'rate', {
            type: 'float', options: { precision: 4 },
            errors: [{ check: function (v) { return !!v; }, selector: '#percentRate .null-error' }]
        });

        // add group and dynamic fields
        this.addGroup(['debtDecrease', 'debtIncrease'], this.decreaseIncreaseDateCheck, { parentPeriod: this.mainPeriodField, parentSum: this.loanSumField });

        function getTemplate($clone, blockPiece) {
            $clone.data('rowIndex', blockPiece.index + 1);

            var $comment = $clone.find('.dynamic-list-comment input').change(function () {
                $(this).data('changed', '1');
            }).focus(tools.selectAllText);

            $comment.val($comment.val() + $clone.data('rowIndex'));

            var $sumInput = $clone.find('.dynamic-list-sum input');
            $sumInput.blur(function () {
                if ($(this).val() === '') {
                    $(this).parent().find('i').removeClass('active');
                }
            }).keyup(function () {
                var $rubbleIcon = $(this).parent().find('i');
                if ($(this).val() === '') {
                    $rubbleIcon.removeClass('active');
                } else {
                    $rubbleIcon.addClass('active');
                }
            });

            return { block: $clone };
        }

        this.addInputField('#debtDecreaseTable', 'debtDecrease', {
            dynamic: true,
            appendButtonSelector: "#addDebtDecrease",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: false,
            prepareTemplate: getTemplate,
            ignoreForEmpty: true,
            fields: {
                date: {
                    selector: ".dynamic-list-date date-picker",
                    options: function ($clone) {
                        return {
                            errors: [
                                { check: function (v) { return v.valid; }, selector: $clone.find('.dynamic-row-error-date') },
                                { check: function (v) { return !v.goesBefore(new DatePoint("01.01.1992")); }, selector: $clone.find('.from-bound-err') }
                            ]
                        }
                    },
                },
                sum: {
                    selector: ".dynamic-list-sum input",
                    options: function ($clone) {
                        return {
                            type: 'money',
                            options: { maxVal: 999999999999.99 },
                            errors: [{ check: function (v) { return !!v; }, selector: $clone.find('.dynamic-row-error-sum') }]
                        }
                    },
                },
                comment: {
                    selector: ".dynamic-list-comment input",
                    options: function ($clone) {
                        return {
                            type: 'text'
                        }
                    },
                }
            }
        });

        this.addInputField('#debtIncreaseTable', 'debtIncrease', {
            dynamic: true,
            appendButtonSelector: "#addDebtIncrease",
            removeSelector: ".dynamic-list-row-remove a",
            keepOne: false,
            prepareTemplate: getTemplate,
            ignoreForEmpty: true,
            fields: {
                date: {
                    selector: ".dynamic-list-date date-picker",
                    options: function ($clone) {
                        return {
                            errors: [
                                { check: function (v) { return v.valid; }, selector: $clone.find('.dynamic-row-error-date') },
                                { check: function (v) { return !v.goesBefore(new DatePoint("01.01.1992")); }, selector: $clone.find('.from-bound-err') }
                            ]
                        }
                    },
                },
                sum: {
                    selector: ".dynamic-list-sum input",
                    options: function ($clone) {
                        return {
                            type: 'money',
                            options: { maxVal: 999999999999.99 },
                            errors: [{ check: function (v) { return !!v; }, selector: $clone.find('.dynamic-row-error-sum') }]
                        }
                    },
                },
                comment: {
                    selector: ".dynamic-list-comment input",
                    options: function ($clone) {
                        return {
                            type: 'text'
                        }
                    },
                }
            }
        });

        // add output fields
        this.addOutputField('.rate-render', 'rate', { type: 'percent', minPrecision: 0, maxPrecision: 4 });
        this.addOutputField('#dateToRender', 'mainPeriod', { type: 'dateToRender', render: function (r) { return r ? r.dateTo.toString() : null } });
        this.addOutputField('#resultDateToRender', 'resultDateTo', { type: 'resultDateToRender', render: function (r) { return r ? r.toString() : null } });

        this.addOutputField('.detalization', 'detalization', {
            html: true,
            render: function (periods) {
                var needHeader = true;

                function renderHeader(params) {
                    needHeader = false;

                    var inner = '';
                    if (params) {
                        inner += '<tr class="spacer"><td colspan="6"></td></tr><tr class="year-info-row"><td colspan="6"><span>' + params.year + '</span><span> (' + params.days + ' дней)</span></td>';
                    }
                    inner += '<tr class="header-row"><td>период</td><td>дней</td><td>сумма займа</td><td>проценты</td><td>сумма проц.</td><td>заем и проц.</td></tr>';

                    return inner;
                }

                function renderRow(period, needBorder) {
                    var localeLoanSum = tools.getLocaleSum(period.loanSum);
                    var localeRate = tools.getLocaleNumber(self.rate);

                    if (self.calcType.value === 'typeDate') {
                        period.period = period.span.toLocaleFormat();
                    } else if (self.calcType.value === 'typeMonth') {
                        if (period.span) {
                            period.period = period.span.toLocaleFormat();
                        } else {
                            period.period = period.month;
                        }
                    } else if (self.calcType.value === 'typeQuarter') {
                        if (period.span) {
                            period.period = period.span.toLocaleFormat();
                        } else {
                            period.period = period.quarter + " квартал";
                        }
                    }

                    return '<tr class="data-row">'
                        + '<td class="data-row-period left' + ((self.calcType.value !== 'typeDate' && !period.span && (self.debtDecrease || self.debtIncrease)) ? ' bigger' : '') + '">' + period.period
                        + '</td><td class="right">' + period.days
                        + '</td><td class="right">' + localeLoanSum
                        + '</td><td class="right" title="' + localeLoanSum + ' &times; ' + (self.isYearRate ? (period.days + '/' + period.daysInYear) : period.days) + ' &times; ' + localeRate + '%"' + '>' + tools.getLocaleSum(period.percent)
                        + '</td><td class="right">' + tools.getLocaleSum(period.totalPercent)
                        + '</td><td class="right">' + tools.getLocaleSum(period.debtSum)
                        + '</td></tr>' + (needBorder ? '<tr class="spacer with-border"><td colspan="6"></td></tr><tr class="lower-small-spacer"><td colspan="6"></td></tr>' : '');
                }

                function renderDecreaseIncreaseRow(period, borderClass, firstBorder, lastBorder, beforeExcess) {
                    return ((self.calcType.value === 'typeDate' && firstBorder) ? '<tr class="small-spacer"><td colspan="6"></td></tr>' : '')
                        + '<tr class="single-column ' + (firstBorder ? borderClass : '') + '">'
                        + '<td>' + period.date
                        + '</td><td></td><td class="right">' + (period.isIncrease && period.loanSum > 0 ? '+' : '') + tools.getLocaleSum(period.loanSum)
                        + '</td><td class="right">' + (period.isIncrease ? '' : tools.getLocaleSum(period.percent))
                        + '</td><td class="right">' + (period.isIncrease ? '' : tools.getLocaleSum(period.totalPercent))
                        + '</td><td class="right">' + tools.getLocaleSum(period.debtSum)
                        + '</td></tr>'
                        + '<tr class="single-column-title"><td colspan="6">' + (period.isIncrease ? 'Увеличение&nbsp;суммы&nbsp;займа' : 'Досрочная&nbsp;выплата')
                        + '</td></tr><tr class="single-column-last ' + (lastBorder ? borderClass : '') + (beforeExcess ? ' before-excess' : '') + '">'
                        + '<td colspan="6"><span class="' + (period.isIncrease ? 'increase-sum' : 'decrease-sum') + '">' + (period.isIncrease ? '+' : '&minus;') + tools.getLocaleSumInRubbles(period.value) + '</span><span class="single-column-comment">' + period.comment + '</span>'
                        + '</td></tr>';
                }

                function renderExcessSum(period) {
                    return '<tr><td colspan="6" class="excess-sum"><span>Превышение выплаченной суммы над суммой займа и процентов</span><span>' + tools.getLocaleSumInRubbles(period.value) + '</span></td>';
                }

                function renderMonthRow(month) {
                    return '<tr class="month-row"><td colspan="6"><span>' + month + '</span></td>';
                }

                function renderQuarterRow(quarter) {
                    return '<tr class="month-row"><td colspan="6"><span>' + quarter + ' квартал</span></td>';
                }

                var table = document.createElement('table');
                table.classList.add('results-table');

                if (self.calcType.value === 'typeMonth' || self.calcType.value === 'typeQuarter') {
                    table.classList.add('alternative');
                }

                var inner = "";
                periods.forEach(function (period, i) {
                    if (period && period.type === "add") {
                        var headerParams = null;
                        if (i === 0 || (period.year !== periods[i - 1].year)) {
                            headerParams = {};
                            headerParams.year = period.year;
                            headerParams.days = period.daysInYear;
                            needHeader = true;
                        }

                        if (needHeader) {
                            inner += renderHeader(headerParams);
                        }

                        if (self.calcType.value === 'typeMonth' && period.span &&
                            (i == 0 || (period.month !== periods[i - 1].month) || (period.month === periods[i - 1].month && period.year !== periods[i - 1].year))) {
                            period.isPeriodTitle = true;
                            inner += renderMonthRow(period.month);
                        } else if (self.calcType.value === 'typeQuarter' && period.span &&
                            (i == 0 || (period.quarter !== periods[i - 1].quarter) || (period.quarter === periods[i - 1].quarter && period.year !== periods[i - 1].year))) {
                            inner += renderQuarterRow(period.quarter);
                            period.isPeriodTitle = true;
                        }

                        if ((self.calcType.value === 'typeMonth' && periods[i + 1] && periods[i + 1].type === 'add' && period.month !== periods[i + 1].month && (period.span || periods[i + 1].span))
                            || (self.calcType.value === 'typeQuarter' && periods[i + 1] && periods[i + 1].type === 'add' && period.quarter !== periods[i + 1].quarter && (period.span || periods[i + 1].span))) {
                            inner += renderRow(period, true);
                        } else {
                            inner += renderRow(period);
                        }

                    } else if (period.type === "excess") {
                        inner += renderExcessSum(period);
                        needHeader = true;
                    } else {

                        var headerParams = null;
                        if (i === 0 || (period.year !== periods[i - 1].year)) {
                            headerParams = {};
                            headerParams.year = period.year;
                            headerParams.days = period.daysInYear;
                            needHeader = true;
                        }

                        if (needHeader) {
                            inner += renderHeader(headerParams);
                        }

                        if (self.calcType.value === 'typeMonth' && (i == 0 || (period.month !== periods[i - 1].month) || (period.month === periods[i - 1].month && period.year !== periods[i - 1].year))) {
                            inner += renderMonthRow(period.month);
                        } else if (self.calcType.value === 'typeQuarter' && (i == 0 || (period.quarter !== periods[i - 1].quarter) || (period.quarter === periods[i - 1].quarter && period.year !== periods[i - 1].year))) {
                            inner += renderQuarterRow(period.quarter);
                        }

                        if ((periods[i - 1] && periods[i - 1].type === "add" && self.calcType.value === 'typeDate')) {
                            if (periods[i + 1] && periods[i + 1].type === "excess" && (i !== periods.length - 2)) {
                                inner += renderDecreaseIncreaseRow(period, "with-border", true, false, null, periods[i - 1].type === "excess");
                            } else {
                                inner += renderDecreaseIncreaseRow(period, "with-border", true, true, null, periods[i - 1].type === "excess");
                            }
                        } else {
                            if (periods[i + 1] && periods[i + 1].type === "excess" && (i === periods.length - 2)) {
                                inner += renderDecreaseIncreaseRow(period, "with-border", false, true, true);
                            } else {
                                inner += renderDecreaseIncreaseRow(period, "");
                            }

                        }
                    }
                });

                table.innerHTML = inner;
                return table.outerHTML;
            }
        });

        this.addOutputField('#decreaseNote', '', { type: 'note', render: function () { return true; } });

        this.loadDescription();

        this.submitOnClick('#calcSubmit');
        this.addResetButton("#clearCalcInput");
    }

    isReady() {
        return true;
    }

    onReset() {
        $('.calc-div-results').removeClass('show-results');
    }

    generateExportObject() {
        let self = this;
        let result = super.generateExportObject();
        result.type = this.calcType.value;
        result.totalPercent = this.totalPercent;
        result.totalDebt = this.totalDebt;
        result.isYearRate = this.isYearRate;
        result.isClosure = this.isClosure;
        result.isEarlyClosure = this.isEarlyClosure;
        result.debtClosureHasExcess = this.debtClosureHasExcess;
        result.excessSum = this.excessSum;
        result.isEmptyPeriod = this.isEmptyPeriod;
        result.mainPeriodString = this.mainPeriodString;
        result.resultPeriodString = this.resultPeriodString;
        result.render.detalization = tools.deepcopy(this.detalization);
        result.percentNote = tools.convertElemToTextWithLinksForExport($('.percent-note'));

        return result;
    }

    showResults() {
        super.showResults();

        var goalParams = {
            args: {
                Percent: this.rate,
                Percent_period: this.rateType.value === 1 ? "year" : "day",
                Settlement_period: this.calcType.value === "typeDate" ? "date" : (this.calcType.value === "typeMonth" ? "month" : "quarter"),
                Partial_Payment: this.debtDecrease && this.debtDecrease.length > 0 ? true : false,
                Debt_Increase: this.debtIncrease && this.debtIncrease.length > 0 ? true : false
            },
            message: 'Is_Made'
        };
        this.sendStatisticMessage(goalParams);
    }

    get defaultFilled() {
        return ($("#debtDecreaseTable").find('.dynamic-list-row').length > 0) ||
            ($("#debtIncreaseTable").find('.dynamic-list-row').length > 0);
    }

    calculate(params) {
        if (params.autoValidation) {
            return false;
        }

        function getPeriodDates(mainPeriod) {
            var dateFrom = mainPeriod.dateFrom.copy().inc();
            var dateTo = mainPeriod.dateTo.copy();
            var dates = [dateFrom, dateTo.copy().inc()];

            for (var i = dateFrom.date.getFullYear(); i < dateTo.date.getFullYear(); i++) {
                var year = i + 1;
                var date = new DatePoint('01.01.' + year);
                dates.push(date);
            }

            return dates;
        }

        function fixPeriod(dateFrom, dateTo) {
            if (!dateFrom || !dateTo) {
                return null;
            }
            var dtFrom = dateFrom.copy();
            var result_ = dtFrom.getSpan(dateTo.copy());

            return result_;
        }

        function getResultPeriodString(resultSpans, finalSpan) {
            var resultString = "";
            var isComplex = false;
            for (var i = 0; i < resultSpans.length; i++) {
                var span = resultSpans[i];
                if (!span.type && i === 0) {
                    resultString += span.dateFrom.toString();
                    finalSpan.dateFrom = span.dateFrom;
                } else if (span.type === "excess" && resultSpans[i - 1] && resultSpans[i - 1].type !== "excess") {
                    resultString += " - " + span.dateTo.toString();
                    finalSpan.dateTo = span.dateTo;
                } else if (!span.type && resultSpans[i - 1].type === "excess") {
                    resultString += ", " + span.dateFrom.toString();
                    isComplex = true;
                }

                if (!span.type && i === resultSpans.length - 1) {
                    resultString += " - " + span.dateTo.toString();
                    if (!finalSpan.dateTo) {
                        finalSpan.dateTo = span.dateTo;
                    }
                }
            }

            if (!isComplex) {
                resultString = null;
            }

            return resultString;
        }

        let self = this;
        self.resultDateTo = this.mainPeriod.dateTo;
        self.isYearRate = this.rateType.value === 1;
        self.isEmptyPeriod = this.mainPeriod.dateFrom.isEqualTo(this.mainPeriod.dateTo);

        var dates = getPeriodDates(this.mainPeriod);

        var uniqueDecreaseIncreaseDates = [];
        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            if (uniqueDecreaseIncreaseDates.indexOf(date) < 0) {
                uniqueDecreaseIncreaseDates.push(date);
            }
        });

        uniqueDecreaseIncreaseDates.forEach((date) => {
            dates.push(date.inc());
        });

        dates = distinctDates(dates);

        dates.sort(function (a, b) {
            return (a.goesBefore(b) ? -1 : 1);
        });

        self.resultSpans = [];
        self.detalization = [];
        if (this.calcType.value === 'typeDate') {
            this.calculateFullDetalization(dates);
        } else if (this.calcType.value === 'typeMonth') {
            this.calculateMonthDetalization(dates);
        } else if (this.calcType.value === 'typeQuarter') {
            this.calculateQuarterDetalization(dates);
        }

        if (self.totalDebt <= 0) {
            self.isClosure = true;
            self.debtClosureHasExcess = self.totalDebt < 0;
            if (self.debtClosureHasExcess) {
                self.excessSum = 0 - self.totalDebt;
            }
            self.totalDebt = 0;
            if (this.resultDateTo.goesBefore(this.mainPeriod.dateTo)) {
                self.isEarlyClosure = true;

                if (this.mainPeriod.dateFrom.isEqualTo(this.resultDateTo)) {
                    self.isEmptyPeriod = true;
                }
            }
        }

        if (this.calcType.value !== 'typeDate') {
            var lastSegment = self.detalization[self.detalization.length - 1];
            var previousSegment = self.detalization[self.detalization.length - 2];

            if (lastSegment && lastSegment.type === "add") {
                if (!previousSegment ||
                    (this.calcType.value === 'typeMonth' && (lastSegment.month !== previousSegment.month || lastSegment.year !== previousSegment.year)) ||
                    (this.calcType.value === 'typeQuarter' && (lastSegment.quarter !== previousSegment.quarter || lastSegment.year !== previousSegment.year))) {

                    lastSegment.span = null;
                }
            }
        }

        const finalSpan = {};
        const resultPeriodString = getResultPeriodString(self.resultSpans, finalSpan);
        const resultPeriod = fixPeriod(finalSpan.dateFrom, finalSpan.dateTo);     
        this.resultPeriodString = !this.isEmptyPeriod 
            ? `${(resultPeriod && !resultPeriodString ? `${resultPeriod.dateFrom.toString()} – ${resultPeriod.dateTo.toString()}` : resultPeriodString)} (${(resultPeriodString ? "всего " : "")}${tools.verboseCountingValue(self.totalDuration, [['день', 'дня', 'дней']])})`
            : "0 дней";
        this.mainPeriodString = `${self.mainPeriod.dateFrom.toString()} – ${self.mainPeriod.dateTo.toString()}`;
        return true;
    }

    beforeRender() {
        $('.total-debt').prop('title', tools.verboseNumber(this.totalDebt));
        $('.total-percent').prop('title', tools.verboseNumber(this.totalPercent));
    }

    decreaseIncreaseDateCheck(fields, options) {
        let result = {
            valid: true
        }

        var debtInfo = new Object();
        debtInfo.decreaseIncreaseGroups = [];

        let decreaseFields = fields.tree.debtDecrease || [];
        let increaseFields = fields.tree.debtIncrease || [];

        if (fields.length > 0) {

            var decreaseIncreaseGroups = [];
            if (increaseFields.length > 0) {
                increaseFields.forEach(function (field) {
                    var group = { dateField: field.date, sumField: field.sum, isIncrease: true, valid: true, comment: field.comment.getValue() };
                    decreaseIncreaseGroups.push(group);
                    if (group.dateField.valid && group.sumField.valid) {
                        debtInfo.hasDebtIncrease = true;
                    }
                });
            }

            if (decreaseFields.length > 0) {
                decreaseFields.forEach(function (field) {
                    var group = { dateField: field.date, sumField: field.sum, isIncrease: false, valid: true, comment: field.comment.getValue() };
                    decreaseIncreaseGroups.push(group);
                    if (group.dateField.valid && group.sumField.valid) {
                        debtInfo.hasDebtDecrease = true;
                    }
                });
            }

            decreaseIncreaseGroups.sort(function (a, b) {
                return (a.dateField.getValue().goesBefore(b.dateField.getValue()) ? -1 : 1);
            });

            decreaseIncreaseGroups.forEach(function (group, index) {
                let elem = group.dateField.elem;
                let $error = $(elem).closest("tr.dynamic-list-row").find(".dynamic-row-error");
                $error.hide();
                if (group.dateField.valid) {
                    elem.removeClass('err');
                }

                var date = group.dateField.getValue();
                if (options.parentPeriod.valid && group.dateField.valid) {
                    if (date.goesBefore(options.parentPeriod.dateFrom) || options.parentPeriod.dateTo.goesBefore(date)) {
                        elem.addClass('err');
                        $error.html((group.isIncrease ? 'Увеличение суммы займа' : 'Досрочная выплата') + ' не может быть '
                            + (options.parentPeriod.dateTo.goesBefore(date) ? 'позже даты возврата' : 'раньше даты выдачи') + ' займа');
                        $error.show();

                        let sumElem = group.sumField.elem;
                        let $sumError = $(sumElem).closest("tr.dynamic-list-row").find(".dynamic-row-error-sum");
                        $sumError.hide();

                        result.valid = group.valid = false;
                    } else {
                        debtInfo.decreaseIncreaseGroups.push(group);
                    }
                }
            });
        }

        result.artifacts = debtInfo;

        return result;
    }

    calculateFullDetalization(dates) {
        let self = this;
        self.totalPercent = 0;
        self.totalMonthPercent = 0;
        self.totalDebt = 0;
        self.totalDuration = 0;
        self.isClosure = false;
        self.isEarlyClosure = false;
        self.debtClosureHasExcess = false;

        var loanSumDynamic = this.loanSum;
        var debtSumDynamic = this.loanSum;

        var dtFrom = dates.shift();
        var dtTo = dtFrom.copy().dec();

        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            var sum = group.sumField.getValue();

            if (dtTo.isEqualTo(date) && group.isIncrease) {
                loanSumDynamic = tools.roundNumber(loanSumDynamic + sum, 2);
                var segment = new Object();
                if (debtSumDynamic <= 0) {
                    segment.loanSum = loanSumDynamic;
                    debtSumDynamic = loanSumDynamic;
                } else {
                    segment.loanSum = sum;
                    debtSumDynamic = tools.roundNumber(debtSumDynamic + sum, 2);
                }
                segment.date = dtTo.toLocaleFormat();
                segment.year = dtTo.date.getFullYear();
                segment.daysInYear = this.getDaysInYear(dtTo);
                segment.isIncrease = true;
                segment.value = sum;
                segment.debtSum = debtSumDynamic;
                segment.type = "increase";
                segment.comment = group.comment;
                self.detalization.push(segment);
            }
        });

        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            var sum = group.sumField.getValue();

            if (dtTo.isEqualTo(date) && !group.isIncrease) {
                var newDebtSum = tools.roundNumber(debtSumDynamic - sum, 2);
                var segment = new Object();
                if (sum > self.totalPercent) {
                    var percentDiff = sum - self.totalPercent;
                    segment.loanSum = percentDiff > loanSumDynamic ? 0 - loanSumDynamic : 0 - percentDiff;
                    segment.percent = 0 - self.totalPercent;
                    self.totalPercent = 0;
                    loanSumDynamic = newDebtSum;
                } else {
                    segment.percent = 0 - sum;
                    self.totalPercent -= sum;
                }
                segment.debtSum = debtSumDynamic > sum ? newDebtSum : 0;
                segment.date = dtTo.toLocaleFormat();
                segment.year = dtTo.date.getFullYear();
                segment.daysInYear = this.getDaysInYear(dtTo);
                segment.isIncrease = false;
                segment.value = sum;
                segment.totalPercent = self.totalPercent;
                segment.type = "decrease";
                segment.comment = group.comment;
                self.detalization.push(segment);

                debtSumDynamic = newDebtSum;
                self.resultDateTo = dtTo;
            }
        });

        if (debtSumDynamic < 0) {
            var segment = new Object();
            segment.value = 0 - debtSumDynamic;
            segment.year = dtTo.date.getFullYear();
            segment.type = "excess";
            self.detalization.push(segment);
        }

        while (dtFrom) {
            if (dates.length > 0) {
                dtTo = dates[0].copy().dec();
                var daysInYear = this.getDaysInYear(dtFrom);
                if (debtSumDynamic > 0) {

                    var dayRate = self.isYearRate ? (this.rate / daysInYear) : this.rate;
                    dayRate /= 100;

                    var daysInPeriod = dtFrom.distanceTo(dtTo);
                    self.totalDuration += daysInPeriod;
                    var segmentSpan = dtFrom.getSpan(dtTo);
                    self.resultSpans.push(segmentSpan);

                    var percent = tools.roundNumber(loanSumDynamic * dayRate * daysInPeriod, 2);
                    debtSumDynamic = tools.roundNumber(debtSumDynamic + percent, 2);
                    self.totalPercent += percent;

                    var segment = new Object();
                    segment.year = dtFrom.date.getFullYear();
                    segment.daysInYear = daysInYear;
                    segment.span = segmentSpan;
                    segment.days = daysInPeriod;
                    segment.loanSum = loanSumDynamic;
                    segment.percent = percent;
                    segment.totalPercent = self.totalPercent;
                    segment.debtSum = debtSumDynamic;
                    segment.type = "add";

                    self.detalization.push(segment);

                    self.resultDateTo = dtTo;
                }

                this.decreaseIncreaseGroups.forEach((group) => {
                    var date = group.dateField.getValue();
                    var sum = group.sumField.getValue();

                    if (dtTo.isEqualTo(date) && group.isIncrease) {
                        loanSumDynamic = tools.roundNumber(loanSumDynamic + sum, 2);
                        var segment = new Object();
                        if (debtSumDynamic <= 0) {
                            segment.loanSum = loanSumDynamic;
                            debtSumDynamic = loanSumDynamic;
                        } else {
                            segment.loanSum = sum;
                            debtSumDynamic = tools.roundNumber(debtSumDynamic + sum, 2);
                        }
                        segment.date = dtTo.toLocaleFormat();
                        segment.year = dtTo.date.getFullYear();
                        segment.daysInYear = daysInYear;
                        segment.isIncrease = true;
                        segment.value = sum;
                        segment.debtSum = debtSumDynamic;
                        segment.type = "increase";
                        segment.comment = group.comment;
                        self.detalization.push(segment);
                    }
                });

                this.decreaseIncreaseGroups.forEach((group) => {
                    var date = group.dateField.getValue();
                    var sum = group.sumField.getValue();

                    if (dtTo.isEqualTo(date) && !group.isIncrease) {
                        var newDebtSum = tools.roundNumber(debtSumDynamic - sum, 2);
                        var segment = new Object();
                        if (sum > self.totalPercent) {
                            var percentDiff = sum - self.totalPercent;
                            segment.loanSum = percentDiff > loanSumDynamic ? 0 - loanSumDynamic : 0 - percentDiff;
                            segment.percent = 0 - self.totalPercent;
                            self.totalPercent = 0;
                            loanSumDynamic = newDebtSum;
                        } else {
                            segment.percent = 0 - sum;
                            self.totalPercent -= sum;
                        }
                        segment.debtSum = debtSumDynamic > sum ? newDebtSum : 0;
                        segment.date = dtTo.toLocaleFormat();
                        segment.year = dtTo.date.getFullYear();
                        segment.daysInYear = daysInYear;
                        segment.isIncrease = false;
                        segment.value = sum;
                        segment.totalPercent = self.totalPercent;
                        segment.type = "decrease";
                        segment.comment = group.comment;
                        self.detalization.push(segment);

                        debtSumDynamic = newDebtSum;
                    }
                });
            }

            var lastSegment = self.detalization[self.detalization.length - 1];
            if ((debtSumDynamic <= 0) && (lastSegment.type !== "excess")) {
                if (debtSumDynamic < 0) {
                    var segment = new Object();
                    segment.value = 0 - debtSumDynamic;
                    segment.type = "excess";
                    segment.year = lastSegment.year;
                    self.detalization.push(segment);
                }

                if (self.resultSpans.length !== 0) {
                    self.resultSpans.push({ type: "excess", dateTo: dtTo });
                }
            }

            dtFrom = dates.shift();
        }

        self.totalDebt = debtSumDynamic;
    }

    calculateMonthDetalization(dates) {
        let self = this;

        function getVerbMonth(monthNumber) {
            switch (monthNumber) {
                case 0: return "январь";
                case 1: return "февраль";
                case 2: return "март";
                case 3: return "апрель";
                case 4: return "май";
                case 5: return "июнь";
                case 6: return "июль";
                case 7: return "август";
                case 8: return "сентябрь";
                case 9: return "октябрь";
                case 10: return "ноябрь";
                case 11: return "декабрь";
            }
        }

        self.totalPercent = 0;
        self.totalDebt = 0;
        self.totalDuration = 0;
        self.isClosure = false;
        self.isEarlyClosure = false;
        self.debtClosureHasExcess = false;
        self.resultDateTo = this.mainPeriod.dateTo;

        var loanSumDynamic = this.loanSum;
        var debtSumDynamic = this.loanSum;

        var dtFrom = dates.shift();
        var dtTo = dtFrom.copy().dec();

        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            var sum = group.sumField.getValue();

            if (dtTo.isEqualTo(date) && group.isIncrease) {
                loanSumDynamic = tools.roundNumber(loanSumDynamic + sum, 2);
                var segment = new Object();
                if (debtSumDynamic <= 0) {
                    segment.loanSum = loanSumDynamic;
                    debtSumDynamic = loanSumDynamic;
                } else {
                    segment.loanSum = sum;
                    debtSumDynamic = tools.roundNumber(debtSumDynamic + sum, 2);
                }
                segment.date = dtTo.toLocaleFormat();
                segment.year = dtTo.date.getFullYear();
                segment.daysInYear = this.getDaysInYear(dtTo);
                segment.month = getVerbMonth(dtTo.date.getMonth());
                segment.isIncrease = true;
                segment.value = sum;
                segment.debtSum = debtSumDynamic;
                segment.type = "increase";
                segment.comment = group.comment;
                self.detalization.push(segment);
            }
        });

        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            var sum = group.sumField.getValue();

            if (dtTo.isEqualTo(date) && !group.isIncrease) {
                var newDebtSum = tools.roundNumber(debtSumDynamic - sum, 2);
                var segment = new Object();
                if (sum > self.totalPercent) {
                    var percentDiff = sum - self.totalPercent;
                    segment.loanSum = percentDiff > loanSumDynamic ? 0 - loanSumDynamic : 0 - percentDiff;
                    segment.percent = 0 - self.totalPercent;
                    self.totalPercent = 0;
                    loanSumDynamic = newDebtSum;
                } else {
                    segment.percent = 0 - sum;
                    self.totalPercent -= sum;
                }
                segment.debtSum = debtSumDynamic > sum ? newDebtSum : 0;
                segment.date = dtTo.toLocaleFormat();
                segment.year = dtTo.date.getFullYear();
                segment.daysInYear = this.getDaysInYear(dtTo);
                segment.month = getVerbMonth(dtTo.date.getMonth());
                segment.isIncrease = false;
                segment.value = sum;
                segment.totalPercent = self.totalPercent;
                segment.type = "decrease";
                segment.comment = group.comment;
                self.detalization.push(segment);

                debtSumDynamic = newDebtSum;
                self.resultDateTo = dtTo;
            }
        });

        if (debtSumDynamic < 0) {
            var segment = new Object();
            segment.value = 0 - debtSumDynamic;
            segment.type = "excess";
            segment.year = dtTo.date.getFullYear();
            segment.month = getVerbMonth(dtTo.date.getMonth());
            self.detalization.push(segment);
        }

        while (dtFrom) {
            if (dates.length > 0) {
                dtTo = dates[0].copy().dec();
                var daysInYear = this.getDaysInYear(dtFrom);
                if (debtSumDynamic > 0) {
                    var dayRate = self.isYearRate ? (this.rate / daysInYear) : this.rate;
                    dayRate /= 100;

                    var daysInPeriod = dtFrom.distanceTo(dtTo);
                    self.totalDuration += daysInPeriod;
                    var segmentSpan = dtFrom.getSpan(dtTo);
                    self.resultSpans.push(segmentSpan);

                    var monthNumber = dtFrom.date.getMonth();
                    var yearNumber = dtFrom.date.getFullYear();
                    var remainingDays = dtFrom.getDaysInMonth() - dtFrom.fixDayMonthYear(1).distanceTo(dtFrom) + 1;
                    var firstDate = dtFrom.copy();
                    while (daysInPeriod > 0) {
                        var dayCount;
                        var lastDate;
                        var span = null;
                        if (remainingDays > daysInPeriod) {
                            dayCount = daysInPeriod;
                            lastDate = dtTo;
                            span = firstDate.getSpan(lastDate);
                        } else {
                            dayCount = remainingDays;
                            lastDate = new DatePoint(new Date(yearNumber, monthNumber + 1, 1)).dec();
                            var lastSegment = self.detalization[self.detalization.length - 1];
                            if (lastSegment && (lastSegment.type === "increase" || lastSegment.type === "decrease") &&
                                dayCount !== firstDate.getDaysInMonth()) {
                                span = firstDate.getSpan(lastDate);
                            }
                        }

                        var monthPercent = tools.roundNumber(loanSumDynamic * dayRate * dayCount, 2);
                        debtSumDynamic = tools.roundNumber(debtSumDynamic + monthPercent, 2);
                        self.totalPercent += monthPercent;

                        var monthSegment = new Object();
                        monthSegment.daysInYear = daysInYear;
                        monthSegment.span = span;
                        monthSegment.month = getVerbMonth(monthNumber);
                        monthSegment.type = "add";
                        monthSegment.year = yearNumber;
                        monthSegment.days = dayCount;
                        monthSegment.loanSum = loanSumDynamic;
                        monthSegment.percent = monthPercent;
                        monthSegment.totalPercent = self.totalPercent;
                        monthSegment.debtSum = debtSumDynamic;

                        self.detalization.push(monthSegment);

                        monthNumber++;
                        if (monthNumber === 12) {
                            monthNumber = 0;
                            yearNumber++;
                        }

                        daysInPeriod -= dayCount;

                        firstDate = new DatePoint(new Date(yearNumber, monthNumber, 1));
                        remainingDays = firstDate.getDaysInMonth();
                    }

                    self.resultDateTo = dtTo;
                }

                this.decreaseIncreaseGroups.forEach((group) => {
                    var date = group.dateField.getValue();
                    var sum = group.sumField.getValue();

                    if (dtTo.isEqualTo(date) && group.isIncrease) {
                        loanSumDynamic = tools.roundNumber(loanSumDynamic + sum, 2);
                        var segment = new Object();
                        if (debtSumDynamic <= 0) {
                            segment.loanSum = loanSumDynamic;
                            debtSumDynamic = loanSumDynamic;
                        } else {
                            segment.loanSum = sum;
                            debtSumDynamic = tools.roundNumber(debtSumDynamic + sum, 2);
                        }
                        segment.date = dtTo.toLocaleFormat();
                        segment.year = dtTo.date.getFullYear();
                        segment.month = getVerbMonth(dtTo.date.getMonth());
                        segment.daysInYear = daysInYear;
                        segment.isIncrease = true;
                        segment.value = sum;
                        segment.debtSum = debtSumDynamic;
                        segment.type = "increase";
                        segment.comment = group.comment;
                        self.detalization.push(segment);
                    }
                });

                this.decreaseIncreaseGroups.forEach((group) => {
                    var date = group.dateField.getValue();
                    var sum = group.sumField.getValue();

                    if (dtTo.isEqualTo(date) && !group.isIncrease) {
                        var newDebtSum = tools.roundNumber(debtSumDynamic - sum, 2);
                        var segment = new Object();
                        if (sum > self.totalPercent) {
                            var percentDiff = sum - self.totalPercent;
                            segment.loanSum = percentDiff > loanSumDynamic ? 0 - loanSumDynamic : 0 - percentDiff;
                            segment.percent = 0 - self.totalPercent;
                            self.totalPercent = 0;
                            loanSumDynamic = newDebtSum;
                        } else {
                            segment.percent = 0 - sum;
                            self.totalPercent -= sum;
                        }
                        segment.debtSum = debtSumDynamic > sum ? newDebtSum : 0;
                        segment.date = dtTo.toLocaleFormat();
                        segment.year = dtTo.date.getFullYear();
                        segment.month = getVerbMonth(dtTo.date.getMonth());
                        segment.daysInYear = daysInYear;
                        segment.isIncrease = false;
                        segment.value = sum;
                        segment.totalPercent = self.totalPercent;
                        segment.type = "decrease";
                        segment.comment = group.comment;
                        self.detalization.push(segment);

                        debtSumDynamic = newDebtSum;
                    }
                });
            }

            var lastSegment = self.detalization[self.detalization.length - 1];
            if ((debtSumDynamic <= 0) && (lastSegment.type !== "excess")) {
                if (debtSumDynamic < 0) {
                    var segment = new Object();
                    segment.value = 0 - debtSumDynamic;
                    segment.type = "excess";
                    segment.year = lastSegment.year;
                    segment.month = lastSegment.month;
                    self.detalization.push(segment);
                }

                if (self.resultSpans.length !== 0) {
                    self.resultSpans.push({ type: "excess", dateTo: dtTo });
                }
            }

            dtFrom = dates.shift();
        }

        self.totalDebt = debtSumDynamic;
    }

    calculateQuarterDetalization(dates) {
        let self = this;

        self.totalPercent = 0;
        self.totalDebt = 0;
        self.totalDuration = 0;
        self.isClosure = false;
        self.isEarlyClosure = false;
        self.debtClosureHasExcess = false;
        self.resultDateTo = this.mainPeriod.dateTo;

        var loanSumDynamic = this.loanSum;
        var debtSumDynamic = this.loanSum;

        var dtFrom = dates.shift();
        var dtTo = dtFrom.copy().dec();

        function daysLeftInQuarter(currentDate, nexQuarterDate) {
            var firstDate = new DatePoint(currentDate);
            var secondDate = new DatePoint(nexQuarterDate);
            return firstDate.distanceTo(secondDate) - 1;
        }

        function getQuarter(d) {
            return Math.floor((d.date.getMonth() + 3) / 3)
        }

        function getNextQuarterDate(quarter, year) {
            var nextQuarterDate;
            if (quarter == 4) {
                nextQuarterDate = new Date(year + 1, 0, 1);
            } else {
                nextQuarterDate = new Date(year, quarter * 3, 1);
            }

            return new DatePoint(nextQuarterDate).date;
        }

        function getCurrentQuarterDate(quarter, year) {
            return new DatePoint(new Date(year, 3 * quarter - 3, 1)).date;
        }

        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            var sum = group.sumField.getValue();

            if (dtTo.isEqualTo(date) && group.isIncrease) {
                loanSumDynamic = tools.roundNumber(loanSumDynamic + sum, 2);
                var segment = new Object();
                if (debtSumDynamic <= 0) {
                    segment.loanSum = loanSumDynamic;
                    debtSumDynamic = loanSumDynamic;
                } else {
                    segment.loanSum = sum;
                    debtSumDynamic = tools.roundNumber(debtSumDynamic + sum, 2);
                }
                segment.date = dtTo.toLocaleFormat();
                segment.year = dtTo.date.getFullYear();
                segment.daysInYear = this.getDaysInYear(dtTo);
                segment.quarter = getQuarter(dtTo);
                segment.isIncrease = true;
                segment.value = sum;
                segment.debtSum = debtSumDynamic;
                segment.type = "increase";
                segment.comment = group.comment;
                self.detalization.push(segment);
            }
        });

        this.decreaseIncreaseGroups.forEach((group) => {
            var date = group.dateField.getValue();
            var sum = group.sumField.getValue();

            if (dtTo.isEqualTo(date) && !group.isIncrease) {
                var newDebtSum = tools.roundNumber(debtSumDynamic - sum, 2);
                var segment = new Object();
                if (sum > self.totalPercent) {
                    var percentDiff = sum - self.totalPercent;
                    segment.loanSum = percentDiff > loanSumDynamic ? 0 - loanSumDynamic : 0 - percentDiff;
                    segment.percent = 0 - self.totalPercent;
                    self.totalPercent = 0;
                    loanSumDynamic = newDebtSum;
                } else {
                    segment.percent = 0 - sum;
                    self.totalPercent -= sum;
                }
                segment.debtSum = debtSumDynamic > sum ? newDebtSum : 0;
                segment.date = dtTo.toLocaleFormat();
                segment.year = dtTo.date.getFullYear();
                segment.daysInYear = this.getDaysInYear(dtTo);
                segment.quarter = getQuarter(dtTo);
                segment.isIncrease = false;
                segment.value = sum;
                segment.totalPercent = self.totalPercent;
                segment.type = "decrease";
                segment.comment = group.comment;
                self.detalization.push(segment);

                debtSumDynamic = newDebtSum;
                self.resultDateTo = dtTo;
            }
        });

        if (debtSumDynamic < 0) {
            var segment = new Object();
            segment.value = 0 - debtSumDynamic;
            segment.type = "excess";
            segment.year = dtTo.date.getFullYear();
            segment.quarter = getQuarter(dtTo);
            self.detalization.push(segment);
        }

        while (dtFrom) {
            if (dates.length > 0) {
                dtTo = dates[0].copy().dec();
                var daysInYear = this.getDaysInYear(dtFrom);

                if (debtSumDynamic > 0) {

                    var dayRate = self.isYearRate ? (this.rate / daysInYear) : this.rate;
                    dayRate /= 100;

                    var daysInPeriod = dtFrom.distanceTo(dtTo);
                    self.totalDuration += daysInPeriod;
                    var segmentSpan = dtFrom.getSpan(dtTo);
                    self.resultSpans.push(segmentSpan);

                    var yearNumber = dtFrom.date.getFullYear();
                    var quarter = Math.floor((dtFrom.date.getMonth() + 3) / 3);

                    var nextQuarterDate = getNextQuarterDate(quarter, yearNumber);
                    var currentQuarterDate = getCurrentQuarterDate(quarter, yearNumber);
                    var remainingDays = daysLeftInQuarter(dtFrom, nextQuarterDate);

                    var firstDate = dtFrom.copy();
                    while (daysInPeriod > 0) {

                        var dayCount;
                        var lastDate;
                        var span = null;
                        if (remainingDays > daysInPeriod) {
                            dayCount = daysInPeriod;
                            lastDate = dtTo;
                            span = firstDate.getSpan(lastDate);
                        } else {
                            dayCount = remainingDays;
                            lastDate = new DatePoint(new Date(yearNumber, nextQuarterDate.getMonth(), 1)).dec();
                            var lastSegment = self.detalization[self.detalization.length - 1];
                            if (lastSegment && (lastSegment.type === "increase" || lastSegment.type === "decrease") &&
                                !new DatePoint(currentQuarterDate).isEqualTo(new DatePoint(firstDate))) {
                                span = firstDate.getSpan(lastDate);
                            }
                        }

                        var quarterPercent = tools.roundNumber(loanSumDynamic * dayRate * dayCount, 2);

                        debtSumDynamic = tools.roundNumber(debtSumDynamic + quarterPercent, 2);
                        self.totalPercent += quarterPercent;

                        var quarterSegment = new Object();
                        quarterSegment.daysInYear = daysInYear;
                        quarterSegment.quarter = quarter;
                        quarterSegment.span = span;
                        quarterSegment.type = "add";
                        quarterSegment.year = yearNumber;
                        quarterSegment.days = dayCount;
                        quarterSegment.loanSum = loanSumDynamic;
                        quarterSegment.percent = quarterPercent;
                        quarterSegment.totalPercent = self.totalPercent;
                        quarterSegment.debtSum = debtSumDynamic;

                        self.detalization.push(quarterSegment);

                        quarter++;
                        if (quarter === 5) {
                            quarter = 1;
                            yearNumber++;
                        }

                        daysInPeriod -= dayCount;

                        firstDate = lastDate.copy().inc();

                        nextQuarterDate = getNextQuarterDate(quarter, yearNumber);
                        remainingDays = daysLeftInQuarter(firstDate.date, nextQuarterDate);
                    }

                    self.resultDateTo = dtTo;
                }

                this.decreaseIncreaseGroups.forEach((group) => {
                    var date = group.dateField.getValue();
                    var sum = group.sumField.getValue();

                    if (dtTo.isEqualTo(date) && group.isIncrease) {
                        loanSumDynamic = tools.roundNumber(loanSumDynamic + sum, 2);
                        var segment = new Object();
                        if (debtSumDynamic <= 0) {
                            segment.loanSum = loanSumDynamic;
                            debtSumDynamic = loanSumDynamic;
                        } else {
                            segment.loanSum = sum;
                            debtSumDynamic = tools.roundNumber(debtSumDynamic + sum, 2);
                        }
                        segment.date = dtTo.toLocaleFormat();
                        segment.year = dtTo.date.getFullYear();
                        segment.quarter = getQuarter(dtTo);
                        segment.daysInYear = daysInYear;
                        segment.isIncrease = true;
                        segment.value = sum;
                        segment.debtSum = debtSumDynamic;
                        segment.type = "increase";
                        segment.comment = group.comment;
                        self.detalization.push(segment);
                    }
                });
                
                this.decreaseIncreaseGroups.forEach((group) => {
                    var date = group.dateField.getValue();
                    var sum = group.sumField.getValue();

                    if (dtTo.isEqualTo(date) && !group.isIncrease) {
                        var newDebtSum = tools.roundNumber(debtSumDynamic - sum, 2);
                        var segment = new Object();
                        if (sum > self.totalPercent) {
                            var percentDiff = sum - self.totalPercent;
                            segment.loanSum = percentDiff > loanSumDynamic ? 0 - loanSumDynamic : 0 - percentDiff;
                            segment.percent = 0 - self.totalPercent;
                            self.totalPercent = 0;
                            loanSumDynamic = newDebtSum;
                        } else {
                            segment.percent = 0 - sum;
                            self.totalPercent -= sum;
                        }
                        segment.debtSum = debtSumDynamic > sum ? newDebtSum : 0;
                        segment.date = dtTo.toLocaleFormat();
                        segment.year = dtTo.date.getFullYear();
                        segment.quarter = getQuarter(dtTo);
                        segment.daysInYear = daysInYear;
                        segment.isIncrease = false;
                        segment.value = sum;
                        segment.totalPercent = self.totalPercent;
                        segment.type = "decrease";
                        segment.comment = group.comment;
                        self.detalization.push(segment);

                        debtSumDynamic = newDebtSum;
                    }
                });
            }

            var lastSegment = self.detalization[self.detalization.length - 1];
            if ((debtSumDynamic <= 0) && (lastSegment.type !== "excess")) {
                if (debtSumDynamic < 0) {
                    var segment = new Object();
                    segment.value = 0 - debtSumDynamic;
                    segment.type = "excess";
                    segment.year = lastSegment.year;
                    segment.quarter = lastSegment.quarter;
                    self.detalization.push(segment);
                }

                if (self.resultSpans.length !== 0) {
                    self.resultSpans.push({ type: "excess", dateTo: dtTo });
                }
            }

            dtFrom = dates.shift();
        }

        self.totalDebt = debtSumDynamic;
    }

    getDaysInYear(date) {
        return date.fixDayMonthYear(1, 1).distanceTo(date.fixDayMonthYear(31, 12));
    }
}

$(function () {
    var calc = new LNPCalculator();
});